import moment from 'moment/moment';
import CrmOrder from './CrmOrder';
import CrmLineItem from './CrmLineItem';
import OrderStatusDisplayType
  from '../components/orders/OrderStatusDisplayType';
import convertNumberToFinancialString from '../utility/convertNumberToFinancialString';

class CrmOrderDetails extends CrmOrder {
  constructor(
    id,
    createdAt,
    orderNumber,
    grandTotal,
    shippingTimestamp,
    shippingStatus,
    poNumber,
    paymentStatus,
    shipmentList,
    shippingAddress,
    billingAddress,
    tax,
    shippingAmount,
    lineItems,
    paymentBalanceDue,
    currency,
    shipMethod = '',
    shipCarrier = '',
    crmOrgId,
    referrerCode,
  ) {
    super(
      id,
      createdAt,
      orderNumber,
      grandTotal,
      shippingTimestamp,
      shippingStatus,
      poNumber,
      paymentStatus,
      shipmentList,
      currency,
      crmOrgId,
    );
    this.shippingAddress = shippingAddress;
    this.billingAddress = billingAddress;
    this.tax = tax || '0';
    this.shippingAmount = shippingAmount || '0';
    this.paymentBalanceDue = paymentBalanceDue;
    this.lineItems = lineItems.map((lineItem) => new CrmLineItem(lineItem.productNumber,
      lineItem.description, lineItem.quantity, lineItem.discount, lineItem.listPrice, currency));
    this.shipMethod = shipMethod;
    this.shipCarrier = shipCarrier;
    this.referrerCode = referrerCode;
  }

  getShippingStatus(numItemsInShipments) {
    const deliveredCount = this.shipmentList
      .reduce((acc, curr) => (curr.isDelivered === true ? acc + 1 : acc), 0);
    const numItemsInOrder = this.lineItems.reduce((acc, curr) => (acc + curr.quantity), 0);

    let numShipped = 0;

    this.shipmentList.forEach((shipment) => {
      if (shipment.shippingDate !== null) {
        numShipped += 1;
      }
    });

    const isPartial = (numItemsInOrder !== numItemsInShipments)
      || (numShipped !== this.shipmentList.length);

    if (this.shippingStatus === 'Cancelled') {
      return OrderStatusDisplayType.CANCELLED;
    }

    if (this.shippingStatus === 'Hold') {
      return OrderStatusDisplayType.HOLD;
    }

    if (this.shippingStatus === 'No Shipment') {
      return OrderStatusDisplayType.COMPLETED;
    }

    if (numShipped === 0 || numItemsInShipments === 0) {
      return OrderStatusDisplayType.RECEIVED;
    }

    if (isPartial) {
      return OrderStatusDisplayType.PARTIAL;
    }

    if (!isPartial && deliveredCount === this.shipmentList.length) {
      return OrderStatusDisplayType.DELIVERED;
    }

    if (!isPartial && deliveredCount !== this.shipmentList.length && numShipped > 0) {
      return OrderStatusDisplayType.SHIPPED;
    }

    // unreachable
    /* istanbul ignore next */
    return OrderStatusDisplayType.UNKNOWN;
  }

  get numItems() {
    return this.lineItems.reduce((acc, curr) => acc + curr.quantity, 0);
  }

  get shippingCostDisplay() {
    return this.shippingAmount
      ? convertNumberToFinancialString(this.shippingAmount, this.currency)
      : convertNumberToFinancialString(0, this.currency);
  }

  get taxDisplay() {
    return this.tax
      ? convertNumberToFinancialString(this.tax, this.currency)
      : convertNumberToFinancialString(0, this.currency);
  }

  get paymentBalanceDueDisplay() {
    return this.paymentBalanceDue
      ? convertNumberToFinancialString(this.paymentBalanceDue, this.currency)
      : convertNumberToFinancialString(0, this.currency);
  }

  get shippingMethod() {
    return this.shipMethod ? this.shipMethod : '';
  }

  get createdAtDisplay() {
    return moment.unix(this.createdAt)
      .format('MMM DD, YYYY');
  }

  get shippingDetailsString() {
    const shippingCarrier = this.shipCarrier != null ? `${this.shipCarrier} ` : '';
    const shippingMethod = this.shipMethod != null ? this.shipMethod : '';
    return shippingCarrier + shippingMethod;
  }

  get needsPayment() {
    const unableToPay = ['cancelled', 'closed', 'paid', 'hold', 'dead', 'refunded'];
    if (!this.paymentStatus) {
      return false;
    }
    if (unableToPay.includes(this.paymentStatus.toLowerCase())) {
      return false;
    }
    return true;
  }
}

export default CrmOrderDetails;
